// src/components/MaintenancePage.jsx
import React from "react";

export default function MaintenancePage({ message }) {
    const displayMessage =
        (message || "").trim() ||
        "We’re upgrading the platform for a faster, smoother experience. Please check back shortly.";

    return (
        <div style={styles.wrapper}>
            {/* subtle background accents */}
            <div aria-hidden="true" style={styles.bgGlowTop} />
            <div aria-hidden="true" style={styles.bgGlowBottom} />
            <div aria-hidden="true" style={styles.bgGrid} />

            <div style={styles.card}>
                <div aria-hidden="true" style={styles.accentLine} />

                <div style={styles.header}>
                    <div style={styles.iconBadge} aria-hidden="true">
                        🚧
                    </div>

                    <div style={styles.titleBlock}>
                        <h1 style={styles.title}>Maintenance Mode</h1>
                        <p style={styles.kicker}>We’ll be right back</p>
                    </div>
                </div>

                {/* ✅ dynamic message */}
                <p style={styles.subtitle}>{displayMessage}</p>

                <div style={styles.statusRow}>
                    <div style={styles.spinner} aria-label="Loading" />
                    <div style={styles.statusText}>
                        <div style={styles.statusLabel}>Working on it…</div>
                        <div style={styles.statusHint}>Thanks for your patience.</div>
                    </div>
                </div>

                <div style={styles.divider} />

                <div style={styles.footer}>
                    <span style={styles.badge}>VSN</span>
                    <span style={styles.noteText}>
                        Thank you for your understanding 😊
                    </span>
                </div>
            </div>
        </div>
    );
}

const YELLOW = "#FFD700";
const BLACK = "#000000";
const WHITE = "#FFFFFF";

const styles = {
    wrapper: {
        minHeight: "100vh",
        position: "relative",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "28px",
        background: BLACK,
        color: WHITE,
        fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif",
    },

    bgGlowTop: {
        position: "absolute",
        inset: "-20% -10% auto -10%",
        height: "60%",
        background:
            "radial-gradient(closest-side at 50% 50%, rgba(255,215,0,0.14), rgba(0,0,0,0) 70%)",
        filter: "blur(22px)",
        pointerEvents: "none",
    },

    bgGlowBottom: {
        position: "absolute",
        inset: "auto -10% -25% -10%",
        height: "65%",
        background:
            "radial-gradient(closest-side at 50% 50%, rgba(255,255,255,0.06), rgba(0,0,0,0) 72%)",
        filter: "blur(26px)",
        pointerEvents: "none",
    },

    bgGrid: {
        position: "absolute",
        inset: 0,
        backgroundImage:
            "linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)",
        backgroundSize: "44px 44px",
        opacity: 0.25,
        pointerEvents: "none",
        maskImage:
            "radial-gradient(circle at 50% 45%, rgba(0,0,0,1) 0%, rgba(0,0,0,0.35) 55%, rgba(0,0,0,0) 75%)",
        WebkitMaskImage:
            "radial-gradient(circle at 50% 45%, rgba(0,0,0,1) 0%, rgba(0,0,0,0.35) 55%, rgba(0,0,0,0) 75%)",
    },

    card: {
        width: "100%",
        maxWidth: "560px",
        position: "relative",
        borderRadius: "20px",
        padding: "28px 26px",
        background:
            "linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.03))",
        border: "1px solid rgba(255,215,0,0.35)",
        boxShadow: "0 26px 70px rgba(0,0,0,0.7)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
    },

    accentLine: {
        position: "absolute",
        left: "14px",
        right: "14px",
        top: "12px",
        height: "2px",
        borderRadius: "999px",
        background: `linear-gradient(90deg, rgba(255,215,0,0), ${YELLOW}, rgba(255,215,0,0))`,
        opacity: 0.9,
    },

    header: {
        display: "flex",
        alignItems: "center",
        gap: "14px",
        marginTop: "8px",
        marginBottom: "14px",
    },

    iconBadge: {
        width: "46px",
        height: "46px",
        borderRadius: "14px",
        display: "grid",
        placeItems: "center",
        background: "rgba(255,215,0,0.12)",
        border: "1px solid rgba(255,215,0,0.35)",
        boxShadow: "0 10px 30px rgba(0,0,0,0.55)",
        fontSize: "22px",
    },

    titleBlock: {
        display: "flex",
        flexDirection: "column",
        gap: "2px",
    },

    title: {
        margin: 0,
        fontSize: "30px",
        letterSpacing: "0.4px",
        lineHeight: 1.1,
        color: YELLOW,
    },

    kicker: {
        margin: 0,
        fontSize: "13px",
        letterSpacing: "0.2px",
        color: "rgba(255,255,255,0.85)",
    },

    subtitle: {
        margin: "0 0 18px",
        fontSize: "15px",
        lineHeight: 1.65,
        color: "rgba(255,255,255,0.88)",
    },

    statusRow: {
        display: "flex",
        alignItems: "center",
        gap: "12px",
        padding: "14px",
        borderRadius: "16px",
        border: "1px solid rgba(255,255,255,0.08)",
        background: "rgba(0,0,0,0.35)",
    },

    spinner: {
        width: "42px",
        height: "42px",
        borderRadius: "999px",
        border: "4px solid rgba(255,215,0,0.22)",
        borderTop: `4px solid ${YELLOW}`,
        animation: "vsnSpin 0.85s linear infinite",
    },

    statusText: {
        display: "flex",
        flexDirection: "column",
        gap: "2px",
    },

    statusLabel: {
        fontSize: "13px",
        fontWeight: 700,
        color: WHITE,
    },

    statusHint: {
        fontSize: "12px",
        color: "rgba(255,255,255,0.72)",
    },

    divider: {
        height: "1px",
        margin: "18px 0 14px",
        background:
            "linear-gradient(90deg, rgba(255,215,0,0), rgba(255,215,0,0.28), rgba(255,215,0,0))",
    },

    footer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "10px",
        flexWrap: "wrap",
    },

    badge: {
        padding: "7px 12px",
        borderRadius: "999px",
        background: YELLOW,
        color: BLACK,
        fontWeight: 900,
        fontSize: "12px",
    },

    noteText: {
        fontSize: "12px",
        color: "rgba(255,255,255,0.8)",
    },
};

// Spinner keyframes injection
if (typeof document !== "undefined" && !document.getElementById("vsn-maintenance-spin")) {
    const styleTag = document.createElement("style");
    styleTag.id = "vsn-maintenance-spin";
    styleTag.innerHTML = `
@keyframes vsnSpin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}`;
    document.head.appendChild(styleTag);
}



